import {
  Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  ExpenseSearchHit, HeaderSearchKind, HeaderSearchResponse, SupplierSearchHit, TransactionSearchHit
} from 'src/app/data/model/search-result';
import { SearchService } from 'src/app/data/service/search.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { UserService } from 'src/app/data/service/user.service';
import { BillsToPaySupplierDetailsRequest } from 'src/app/modules/bookkeeping/models/invoice/bills-to-pay-invoice-details-request';
import { RecognizedSupplierService } from 'src/app/modules/payable/services/recognized-supplier.service';
import { EntitlementsService } from 'src/app/core/service/entitlements.service';
import { PermissionsService } from 'src/app/core/service/permissions.service';
import { MODULES } from 'src/app/core/config/module-codes';
import { environment } from 'src/environments/environment';

/** A piece of text, marked where it matched the query. Rendered as text -- never as HTML. */
export interface TextSegment { text: string; match: boolean; }

export type SearchTab = 'all' | HeaderSearchKind;
export type ChipTone = 'success' | 'warn' | 'danger' | 'info' | 'muted';

/** One selectable line in the palette: what the arrow keys move through and Enter opens. */
export interface PaletteRow {
  index: number;
  kind: 'expense' | 'supplier' | 'transaction' | 'recent' | 'action' | 'more' | 'ai';
  title: TextSegment[];
  meta?: TextSegment[];
  detail?: string;
  /** A matched line item, marked where it matched. */
  detailSegments?: TextSegment[];
  amount?: string;
  amountTone?: 'in' | 'out';
  chip?: { label: string; tone: ChipTone };
  avatar?: string;
  avatarClass?: string;
  icon?: string;
  route?: string;
  moreTab?: HeaderSearchKind;
  expense?: ExpenseSearchHit;
  supplier?: SupplierSearchHit;
  transaction?: TransactionSearchHit;
  recent?: string;
}

export interface PaletteSection {
  key: string;
  label: string;
  total?: number;
  rows: PaletteRow[];
}

interface QuickAction { label: string; detail: string; route: string; icon: string; }

interface SearchState { q: string; tab: SearchTab; org: string; attempt: number; }

const RECENT_LIMIT = 6;
const PREVIEW_TAKE = 5;
const TAB_TAKE = 50;

/**
 * SD-1245: the header search, as a command palette.
 *
 * One request per pause in typing (`GET api/Search/global`) returns the best expenses, contacts
 * (the API's "suppliers") and bank transactions, ranked and already narrowed by the server to
 * what the user may see. A result opens in a side panel over whatever page the user is on.
 * Keyboard first: Ctrl/Cmd+K or "/" to open, arrows to move, Enter to open, Tab to switch kind,
 * Esc to clear and close.
 */
@Component({
  selector: 'app-main-search',
  templateUrl: './MainSearch.component.html',
  styleUrls: ['./MainSearch.component.scss']
})
export class MainSearchComponent implements OnInit, OnDestroy {
  @Output() showContactForm = new EventEmitter<boolean>();
  @Output() openAiPanel = new EventEmitter<string>();
  /** A result opens in a side panel over the page the user is on; the host owns the panels. */
  @Output() openExpense = new EventEmitter<ExpenseSearchHit>();
  @Output() openTransaction = new EventEmitter<TransactionSearchHit>();

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef<HTMLInputElement>;
  @ViewChild('panelBody') panelBody: ElementRef<HTMLDivElement>;

  readonly isChromeEnv: boolean = environment.isChromeEnv;
  readonly shortcutLabel: string = /Mac|iPhone|iPad/i.test(navigator.platform || '') ? '⌘K' : 'Ctrl K';

  query = '';
  open = false;
  tab: SearchTab = 'all';
  loading = false;
  failed = false;
  response: HeaderSearchResponse | null = null;
  /** Whether the server searches transactions for this user; known after the first search. */
  transactionsAvailable = true;

  sections: PaletteSection[] = [];
  navRows: PaletteRow[] = [];
  activeIndex = -1;

  recentSearches: string[] = [];
  quickActions: QuickAction[] = [];
  currentUser: any = null;

  private readonly search$ = new Subject<SearchState>();
  private attempt = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly _host: ElementRef<HTMLElement>,
    private readonly _router: Router,
    private readonly _searchService: SearchService,
    private readonly _organizationService: OrganizationService,
    private readonly _userService: UserService,
    private readonly _suppliers: RecognizedSupplierService,
    private readonly _entitlements: EntitlementsService,
    private readonly _permissions: PermissionsService,
  ) { }

  // ── Lifecycle ──────────────────────────────────────────────

  ngOnInit(): void {
    this._userService.loggedUserDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.currentUser = user || null;
        this.loadRecents();
        this.rebuild();
      });

    // A new organisation: its results and recent searches are not this one's.
    this._organizationService.selectedOrganizationId
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.response = null;
        this.transactionsAvailable = true;
        this.loadRecents();
        if (this.query.trim()) { this.search$.next(this.state()); }
        this.rebuild();
      });

    this.quickActions$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(actions => {
        this.quickActions = actions;
        this.rebuild();
      });

    this.search$
      .pipe(
        debounceTime(180),
        distinctUntilChanged((a, b) => a.q === b.q && a.tab === b.tab && a.org === b.org && a.attempt === b.attempt),
        tap(state => {
          this.loading = this.isSearchable(state.q);
          this.failed = false;
        }),
        switchMap(state => this.isSearchable(state.q)
          ? this._searchService.headerSearch({
              organizationId: state.org,
              q: state.q,
              // Every kind each time, so the tab counts always describe this query; a tab only asks for more of them.
              take: state.tab === 'all' ? PREVIEW_TAKE : TAB_TAKE,
            }).pipe(
              map(response => ({ state, response, failed: false })),
              catchError(() => of({ state, response: null as HeaderSearchResponse, failed: true })))
          : of({ state, response: null as HeaderSearchResponse, failed: false })),
        takeUntil(this.destroy$))
      .subscribe(result => {
        this.loading = false;
        this.failed = result.failed;
        if (result.response) {
          this.response = result.response;
          this.transactionsAvailable = result.response.transactions.searched;
        } else if (!result.failed) {
          this.response = null;
        }
        this.rebuild();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Opening, closing and the keyboard ───────────────────────

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    const key = (event.key || '').toLowerCase();
    if ((event.metaKey || event.ctrlKey) && key === 'k') {
      event.preventDefault();
      this.focus();
      return;
    }
    if (key === '/' && !event.metaKey && !event.ctrlKey && !this.isTyping(event.target)) {
      event.preventDefault();
      this.focus();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMousedown(event: MouseEvent): void {
    // The path, not contains(): a target re-rendered during this same mousedown is no longer in the host.
    const path = event.composedPath ? event.composedPath() : [];
    const inside = path.indexOf(this._host.nativeElement) >= 0 || this._host.nativeElement.contains(event.target as Node);
    if (this.open && !inside) {
      this.close();
    }
  }

  onFocus(): void {
    this.open = true;
    this.rebuild();
  }

  onQueryChange(value: string): void {
    this.query = value || '';
    this.open = true;
    if (!this.query.trim()) {
      this.tab = 'all';
      this.loading = false;
      this.failed = false;
    }
    this.search$.next(this.state());
    this.rebuild();
  }

  onInputKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.open = true;
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.move(-1);
        break;
      case 'Enter': {
        event.preventDefault();
        const row = this.navRows[this.activeIndex];
        if (row) {
          this.openRow(row);
        } else if (this.query.trim()) {
          this.remember(this.query);
        }
        break;
      }
      case 'Tab':
        if (this.open && this.hasQuery) {
          event.preventDefault();
          this.cycleTab(event.shiftKey ? -1 : 1);
        }
        break;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        if (this.query) {
          this.clear();
        } else {
          this.close();
          this.searchInput.nativeElement.blur();
        }
        break;
    }
  }

  focus(): void {
    this.searchInput.nativeElement.focus();
    this.searchInput.nativeElement.select();
    this.open = true;
    this.rebuild();
  }

  clear(): void {
    this.onQueryChange('');
    this.searchInput.nativeElement.focus();
  }

  close(): void {
    this.open = false;
    this.activeIndex = -1;
  }

  retry(): void {
    this.attempt++;
    this.search$.next(this.state());
    this.searchInput.nativeElement.focus();
  }

  setTab(tab: SearchTab): void {
    if (this.tab === tab) { return; }
    this.tab = tab;
    this.search$.next(this.state());
    this.rebuild();
    this.searchInput.nativeElement.focus();
  }

  // ── Opening a row ──────────────────────────────────────────

  openRow(row: PaletteRow): void {
    switch (row.kind) {
      case 'recent':
        this.onQueryChange(row.recent);
        this.searchInput.nativeElement.focus();
        return;
      case 'more':
        this.setTab(row.moreTab);
        return;
      case 'ai':
        this.remember(this.query);
        this.close();
        this.openAiPanel.emit(this.query.trim());
        return;
      case 'action':
        this.close();
        this._router.navigateByUrl(row.route);
        return;
      case 'expense':
        this.remember(this.query);
        this.close();
        this.openExpense.emit(row.expense);
        return;
      case 'supplier':
        this.remember(this.query);
        this.close();
        this._suppliers.sendSupplierToContactForm({
          supplierId: row.supplier.supplierId,
          invoiceColorNumber: 1
        } as BillsToPaySupplierDetailsRequest);
        this.showContactForm.emit(true);
        return;
      case 'transaction':
        this.remember(this.query);
        this.close();
        this.openTransaction.emit(row.transaction);
        return;
    }
  }

  removeRecent(term: string, event: Event): void {
    event.stopPropagation();
    this.recentSearches = this.recentSearches.filter(s => s !== term);
    this.saveRecents();
    this.rebuild();
  }

  clearRecents(): void {
    this.recentSearches = [];
    this.saveRecents();
    this.rebuild();
  }

  // ── What the template reads ────────────────────────────────

  get hasQuery(): boolean {
    return !!this.query.trim();
  }

  get canUseAi(): boolean {
    // SD-1244: the user's AI setting in this organisation (super admins always); the API enforces it too.
    return !!(this.currentUser?.aiUser || this.currentUser?.role === 1);
  }

  get tabs(): { key: SearchTab; label: string; count: number | null }[] {
    const r = this.response;
    const tabs: { key: SearchTab; label: string; count: number | null }[] = [
      { key: 'all', label: 'All', count: null },
      { key: 'expenses', label: 'Expenses', count: r ? r.expenses.total : null },
      { key: 'suppliers', label: 'Contacts', count: r ? r.suppliers.total : null },
    ];
    // Transactions only where the server searched them (the module and permission allow it).
    if (this.transactionsAvailable) {
      tabs.push({ key: 'transactions', label: 'Transactions', count: r ? r.transactions.total : null });
    }
    return tabs;
  }

  /** Said once, so a Submitter knows the list is theirs rather than everything. */
  get narrowedNote(): string | null {
    const r = this.response;
    if (!r) { return null; }
    const expenses = r.expenses.narrowed || r.suppliers.narrowed;
    const transactions = r.transactions.narrowed;
    if (expenses && transactions) { return 'Showing expenses you submitted or were assigned, and transactions assigned to you.'; }
    if (expenses) { return 'Showing expenses you submitted or were assigned, and their contacts.'; }
    if (transactions) { return 'Showing transactions on your accounts or assigned to you.'; }
    return null;
  }

  get resultCount(): number {
    const r = this.response;
    return r ? r.expenses.total + r.suppliers.total + r.transactions.total : 0;
  }

  get showEmpty(): boolean {
    return this.hasQuery && !this.loading && !this.failed && !!this.response && this.resultCount === 0;
  }

  get tooShort(): boolean {
    return this.hasQuery && !this.isSearchable(this.query.trim());
  }

  /** `tabs` is rebuilt on every read; without this each change detection replaced the buttons mid-click. */
  trackTab(_: number, tab: { key: SearchTab }): string {
    return tab.key;
  }

  trackRow(_: number, row: PaletteRow): string {
    return `${row.kind}:${row.index}:${row.expense?.invoiceId || row.supplier?.supplierId || row.transaction?.transactionId || row.recent || row.route || ''}`;
  }

  // ── Building the rows ──────────────────────────────────────

  private rebuild(): void {
    const sections: PaletteSection[] = [];
    let index = 0;
    const add = (row: Omit<PaletteRow, 'index'>): PaletteRow => ({ ...row, index: index++ } as PaletteRow);

    if (!this.hasQuery) {
      if (this.recentSearches.length) {
        sections.push({
          key: 'recent',
          label: 'Recent searches',
          rows: this.recentSearches.map(term => add({ kind: 'recent', title: [{ text: term, match: false }], recent: term, icon: 'recent' }))
        });
      }
      if (this.quickActions.length) {
        sections.push({
          key: 'actions',
          label: 'Jump to',
          rows: this.quickActions.map(a => add({
            kind: 'action', title: [{ text: a.label, match: false }], detail: a.detail, route: a.route, icon: a.icon
          }))
        });
      }
    } else if (this.response && !this.failed && !this.tooShort) {
      const r = this.response;
      const showAll = this.tab === 'all';

      if ((showAll || this.tab === 'expenses') && r.expenses.items.length) {
        const rows = r.expenses.items.map(e => add(this.expenseRow(e)));
        if (showAll && r.expenses.total > rows.length) {
          rows.push(add(this.moreRow('expenses', r.expenses.total, 'expenses')));
        }
        sections.push({ key: 'expenses', label: 'Expenses', total: r.expenses.total, rows });
      }
      if ((showAll || this.tab === 'transactions') && r.transactions.items.length) {
        const rows = r.transactions.items.map(t => add(this.transactionRow(t)));
        if (showAll && r.transactions.total > rows.length) {
          rows.push(add(this.moreRow('transactions', r.transactions.total, 'transactions')));
        }
        sections.push({ key: 'transactions', label: 'Transactions', total: r.transactions.total, rows });
      }
      if ((showAll || this.tab === 'suppliers') && r.suppliers.items.length) {
        const rows = r.suppliers.items.map(s => add(this.supplierRow(s)));
        if (showAll && r.suppliers.total > rows.length) {
          rows.push(add(this.moreRow('suppliers', r.suppliers.total, 'contacts')));
        }
        sections.push({ key: 'suppliers', label: 'Contacts', total: r.suppliers.total, rows });
      }
    }

    if (this.canUseAi) {
      const q = this.query.trim();
      sections.push({
        key: 'ai',
        label: '',
        rows: [add({
          kind: 'ai',
          title: q ? [{ text: 'Ask AI about ', match: false }, { text: `“${q}”`, match: true }] : [{ text: 'Ask AI anything about your books', match: false }],
          icon: 'ai'
        })]
      });
    }

    this.sections = sections;
    this.navRows = sections.reduce((all, s) => all.concat(s.rows), [] as PaletteRow[]);
    // Typing puts the best match under Enter; an empty box selects nothing until the arrows move.
    this.activeIndex = this.hasQuery && this.navRows.length && this.navRows[0].kind !== 'ai' ? 0 : -1;
  }

  private expenseRow(e: ExpenseSearchHit): Omit<PaletteRow, 'index'> {
    const meta: TextSegment[] = [];
    if (e.invoiceNumber) { meta.push(...this.highlight(e.invoiceNumber)); }
    const rest = [e.documentType, this.formatDate(e.date)].filter(Boolean).join(' · ');
    if (rest) { meta.push({ text: (meta.length ? ' · ' : '') + rest, match: false }); }
    return {
      kind: 'expense',
      title: this.highlight(e.supplierName || 'Unknown supplier'),
      meta,
      detail: e.matchedLine || null,
      detailSegments: e.matchedLine ? this.highlight(e.matchedLine) : null,
      amount: e.total != null ? this.formatMoney(e.total, e.currency) : null,
      chip: this.expenseChip(e),
      avatar: e.supplierName || '?',
      avatarClass: e.hexColorClass || 'sup_img_box_1',
      expense: e,
    };
  }

  private supplierRow(s: SupplierSearchHit): Omit<PaletteRow, 'index'> {
    const bills = s.billCount === 1 ? '1 expense' : `${s.billCount || 0} expenses`;
    const last = s.lastBillDate ? ` · last ${this.formatDate(s.lastBillDate)}` : '';
    return {
      kind: 'supplier',
      title: this.highlight(s.name || 'Unnamed contact'),
      meta: s.email ? this.highlight(s.email) : null,
      detail: bills + last,
      avatar: s.name || '?',
      avatarClass: s.hexColorClass || 'sup_img_box_1',
      supplier: s,
    };
  }

  private transactionRow(t: TransactionSearchHit): Omit<PaletteRow, 'index'> {
    const received = t.amount > 0;
    const title = t.relatedName || t.description || 'Bank transaction';
    const meta: TextSegment[] = [];
    if (t.relatedName && t.description) { meta.push(...this.highlight(t.description)); }
    const rest = [t.accountName, this.formatDate(t.transactionDate)].filter(Boolean).join(' · ');
    if (rest) { meta.push({ text: (meta.length ? ' · ' : '') + rest, match: false }); }
    return {
      kind: 'transaction',
      title: this.highlight(title),
      meta,
      amount: (received ? '+' : '−') + this.formatMoney(Math.abs(t.amount), null),
      amountTone: received ? 'in' : 'out',
      chip: t.isReconciled ? { label: 'Reconciled', tone: 'success' } : { label: 'To reconcile', tone: 'warn' },
      icon: received ? 'in' : 'out',
      transaction: t,
    };
  }

  private moreRow(tab: HeaderSearchKind, total: number, noun: string): Omit<PaletteRow, 'index'> {
    return { kind: 'more', title: [{ text: `See all ${total} ${noun}`, match: false }], moreTab: tab, icon: 'more' };
  }

  private expenseChip(e: ExpenseSearchHit): { label: string; tone: ChipTone } | null {
    if (!e.processed) { return { label: 'In inbox', tone: 'info' }; }
    const status = (e.status || '').trim().toLowerCase();
    if (status === 'paid') { return { label: 'Paid', tone: 'success' }; }
    if (status === 'voided' || status === 'deleted') { return { label: 'Voided', tone: 'muted' }; }
    if (status === 'draft') { return { label: 'Draft', tone: 'muted' }; }
    if (status === 'awaiting payment' || status === 'authorised' || status === 'submitted') {
      const due = e.dueDate ? new Date(e.dueDate) : null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return due && !isNaN(due.getTime()) && due < today
        ? { label: 'Overdue', tone: 'danger' }
        : { label: 'Awaiting payment', tone: 'warn' };
    }
    return status ? { label: e.status, tone: 'muted' } : null;
  }

  /** Marks every occurrence of the query's words in the text, case-insensitively. */
  highlight(text: string): TextSegment[] {
    const value = text || '';
    const words = this.query.trim().toLowerCase().split(/\s+/).filter(w => w.length > 0);
    if (!value || !words.length) { return [{ text: value, match: false }]; }

    const lower = value.toLowerCase();
    const marked: boolean[] = new Array(value.length).fill(false);
    for (const word of words) {
      let at = lower.indexOf(word);
      while (at >= 0) {
        for (let i = at; i < at + word.length; i++) { marked[i] = true; }
        at = lower.indexOf(word, at + word.length);
      }
    }

    const segments: TextSegment[] = [];
    for (let i = 0; i < value.length; i++) {
      const last = segments[segments.length - 1];
      if (last && last.match === marked[i]) {
        last.text += value[i];
      } else {
        segments.push({ text: value[i], match: marked[i] });
      }
    }
    return segments;
  }

  // ── Helpers ────────────────────────────────────────────────

  private move(step: number): void {
    const count = this.navRows.length;
    if (!count) { return; }
    this.activeIndex = this.activeIndex < 0
      ? (step > 0 ? 0 : count - 1)
      : (this.activeIndex + step + count) % count;
    setTimeout(() => {
      const el = this._host.nativeElement.querySelector(`#gs-opt-${this.activeIndex}`) as HTMLElement;
      if (el && el.scrollIntoView) { el.scrollIntoView({ block: 'nearest' }); }
    });
  }

  private cycleTab(step: number): void {
    const keys = this.tabs.map(t => t.key);
    const at = keys.indexOf(this.tab);
    this.setTab(keys[(at + step + keys.length) % keys.length]);
  }

  private state(): SearchState {
    return { q: this.query.trim(), tab: this.tab, org: this._organizationService.selectedOrganization, attempt: this.attempt };
  }

  /** Two characters, or a single digit (an amount). */
  private isSearchable(q: string): boolean {
    return !!q && (q.length >= 2 || /^\d$/.test(q));
  }

  private isTyping(target: EventTarget | null): boolean {
    const el = target as HTMLElement;
    if (!el) { return false; }
    const tag = (el.tagName || '').toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
  }

  private quickActions$(): Observable<QuickAction[]> {
    return combineLatest([
      this._entitlements.has$(MODULES.INBOX).pipe(catchError(() => of(false))),
      this._entitlements.has$(MODULES.BILLS_TO_PAY).pipe(catchError(() => of(false))),
      this._permissions.has$('bills.view').pipe(catchError(() => of(false))),
      this._entitlements.has$(MODULES.TRANSACTIONS).pipe(catchError(() => of(false))),
      this._permissions.has$('banking.view').pipe(catchError(() => of(false))),
    ]).pipe(map(([inbox, billsModule, billsView, transactionsModule, bankingView]) => {
      const actions: QuickAction[] = [];
      if (inbox) { actions.push({ label: 'Expenses inbox', detail: 'Review and process expenses', route: '/inbox', icon: 'inbox' }); }
      if (billsModule && billsView) { actions.push({ label: 'Bills to pay', detail: 'Outstanding bills and payments', route: '/payable/outstanding-amounts', icon: 'bills' }); }
      if (transactionsModule && bankingView) { actions.push({ label: 'Transactions', detail: 'Reconcile bank and card transactions', route: '/transactions', icon: 'bank' }); }
      return actions;
    }));
  }

  private formatMoney(value: number, currency: string | null): string {
    const code = (currency || '').trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(code)) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).format(value);
      } catch {
        // Older browsers reject narrowSymbol, and an unknown code throws: fall through.
      }
    }
    return '$' + new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  }

  private formatDate(value: string | null): string {
    if (!value) { return ''; }
    // The server sends dates without a zone; read the date part as a local date so it never shifts a day.
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    const date = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
    if (isNaN(date.getTime())) { return ''; }
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleDateString(undefined, sameYear
      ? { day: 'numeric', month: 'short' }
      : { day: 'numeric', month: 'short', year: 'numeric' });
  }

  // ── Recent searches: per user and organisation, in this browser ──

  private get recentKey(): string | null {
    const user = this.currentUser?.id;
    const org = this._organizationService.selectedOrganization;
    return user && org ? `synced.search.recent:${user}:${org}` : null;
  }

  private loadRecents(): void {
    const key = this.recentKey;
    this.recentSearches = [];
    if (!key) { return; }
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      this.recentSearches = Array.isArray(stored) ? stored.filter(s => typeof s === 'string').slice(0, RECENT_LIMIT) : [];
    } catch {
      this.recentSearches = [];
    }
  }

  private saveRecents(): void {
    const key = this.recentKey;
    if (!key) { return; }
    try {
      localStorage.setItem(key, JSON.stringify(this.recentSearches));
    } catch {
      // Storage full or blocked: recent searches are a convenience.
    }
  }

  private remember(term: string): void {
    const value = (term || '').trim();
    if (value.length < 2) { return; }
    this.recentSearches = [value, ...this.recentSearches.filter(s => s.toLowerCase() !== value.toLowerCase())].slice(0, RECENT_LIMIT);
    this.saveRecents();
  }
}
